import React from "react";
import OnboardingLayout from "./OnboardingLayout";
import { HiArrowRight } from "react-icons/hi";
import { HiOutlineSparkles } from "react-icons/hi2";

/** Step in the onboarding flow where users specify their regions and markets. */
// Constants for available regions and markets
const REGIONS = ["Canada", "United States", "Europe", "Other"];
const MARKETS = [
  "AESO",
  "BPA",
  "CAISO",
  "ERCOT",
  "PJM",
  "SPP",
  "IESO",
  "NEISO",
  "NYISO",
  "Other",
];

// Props for the StepRegion component
interface StepRegionProps {
  regions: string[];
  markets: string[];
  onRegionsChange?: (values: string[]) => void;
  onMarketsChange?: (values: string[]) => void;
  onContinue?: () => void;
  onBack?: () => void;
  hasExistingPersonas?: boolean;
  onExit?: () => void;
}

// Helper function to toggle selection of a label in a list
const toggleValue = (list: string[], label: string) => {
  if (list.includes(label)) {
    return list.filter((item) => item !== label);
  }
  return [...list, label];
};

// StepRegion component definition
function StepRegion({
  regions,
  markets,
  onRegionsChange,
  onMarketsChange,
  onContinue,
  onBack,
  hasExistingPersonas,
  onExit,
}: StepRegionProps) {
  return (
    <OnboardingLayout showProgress progressValue={0.4} hasExistingPersonas={hasExistingPersonas} onExit={onExit}>
      {/* Main content area with padding and spacing */}
      <div className="px-8 pb-10 pt-6 space-y-8">
        {/* Header section with icon and description */}
        <div className="flex items-start gap-4">
          <div className="rounded-3xl bg-[rgba(162,187,191,0.1)] p-2">
            <HiOutlineSparkles className="text-eai-primary-500" size={24} />
          </div>
          <div>
            <p className="text-base font-semibold text-eai-dark">
              Is there a specific region or market you're working in?
            </p>
            <p className="text-sm text-gray-500">
              Select all that apply so we tailor forecasts and data feeds.
            </p>
          </div>
        </div>
       
       {/* Regions selection section */}
        <div>
          <p className="text-sm font-semibold text-eai-dark uppercase tracking-wide">
            Regions
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {REGIONS.map((label) => {
              const isActive = regions.includes(label);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() =>
                    onRegionsChange?.(toggleValue(regions, label))
                  }
                  className={`rounded-xl border px-4 py-3 text-left text-base transition ${
                    isActive
                      ? "border-eai-primary-500 bg-[rgba(162,187,191,0.1)]"
                      : "border-eai-bg-lt2 hover:border-eai-primary-500"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Markets selection section */}
        <div>
          <p className="text-sm font-semibold text-eai-dark uppercase tracking-wide">
            Markets
          </p>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {MARKETS.map((label) => {
              const isActive = markets.includes(label);
              return (
                <button
                  key={label}
                  type="button"
                  onClick={() =>
                    onMarketsChange?.(toggleValue(markets, label))
                  }
                  className={`rounded-xl border px-4 py-3 text-left text-base transition ${
                    isActive
                      ? "border-eai-primary-500 bg-[rgba(162,187,191,0.1)]"
                      : "border-eai-bg-lt2 hover:border-eai-primary-500"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Navigation buttons */}    
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onBack}
            className="rounded-lg border border-eai-bg-lt2 px-4 py-2 text-sm text-eai-dark hover:border-eai-primary"
          >
            Back
          </button>
          <button
            type="button"
            onClick={onContinue}
            className="inline-flex items-center gap-2 rounded-lg bg-eai-primary-accent px-6 py-2 text-sm font-medium text-white transition hover:opacity-90"
          >
            Continue
            <HiArrowRight />
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}

export default StepRegion;
